import { useState } from "react";
import {
  getOrdersByTableApi,
  checkDeliveredOrderApi,
  addOrderToTableApi,
  addPaymentToOrderApi,
  closeOrderApi,
  deleteOrderApi,
  getOrdersByPaymentApi,
  getOrdersInRangeApi,
  getOpenOrdersApi,
  markOrderReadyApi,
  getCancellationsApi,
  getCourtesiesApi,
} from "../api/orders";
import { useAuth } from "./";

export function useOrder() {
  const { auth } = useAuth();
  // Solo con sesión válida (me.id): el Cliente de la mesa no tiene sesión, y
  // un token vencido guardado en ese celular haría que el backend rechace
  // incluso lo que el Cliente sí puede hacer sin login.
  const token = auth?.me?.id ? auth.token : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [orders, setOrders] = useState(null);

  const getOrdersByTable = async (idTable, status, ordering, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getOrdersByTableApi(idTable, status, ordering, token);
      if (!silent) setLoading(false);
      setOrders(response);
    } catch (error) {
      if (!silent) setLoading(false);
      setError(error);
    }
  };

  // Pantalla de cocina: devuelve el resultado (no lo guarda en `orders`) y
  // deja que el error llegue a quien llama, para mostrar "sin conexión".
  const getOpenOrders = () => getOpenOrdersApi(token);
  const markOrderReady = (idOrder) => markOrderReadyApi(idOrder, token);
  const getCancellations = (dateFrom, dateTo) =>
    getCancellationsApi(dateFrom, dateTo, token);
  const getCourtesies = (dateFrom, dateTo) => getCourtesiesApi(dateFrom, dateTo, token);

  // Devuelve { ok, message }: el servidor rechaza entregar lo que cocina aún
  // no marca "Listo" y la pantalla muestra el motivo.
  const checkDeliveredOrder = async (idOrder) => {
    try {
      await checkDeliveredOrderApi(idOrder, token);
      return { ok: true };
    } catch (error) {
      setError(error);
      return { ok: false, message: error.message };
    }
  };

  const addOrderToTable = async (
    idTable,
    idProduct,
    note = "",
    optionChoice = "",
    extras = [],
    price = null,
    offerId = null,
    staffOptions = {}
  ) => {
    try {
      await addOrderToTableApi(
        idTable,
        idProduct,
        note,
        optionChoice,
        extras,
        price,
        offerId,
        token,
        staffOptions
      );
    } catch (error) {
      setError(error);
    }
  };

  const addPaymentToOrder = async (idOrder, idPayment) => {
    try {
      await addPaymentToOrderApi(idOrder, idPayment, token);
    } catch (error) {
      // console.log(error)
      setError(error);
    }
  };

  const closeOrder = async (idOrder) => {
    try {
      await closeOrderApi(idOrder, token);
    } catch (error) {
      setError(error);
    }
  };

  const deleteOrder = async (idOrder, reason = "OTHER", note = "") => {
    try {
      await deleteOrderApi(idOrder, token, reason, note);
    } catch (error) {
      setError(error);
    }
  };

  const getOrdersByPayment = async (idPayment) => {
    try {
      return await getOrdersByPaymentApi(idPayment, token);
    } catch (error) {
      setError(error);
    }
  };

  const getOrdersInRange = async (dateFrom, dateTo) => {
    try {
      setLoading(true);
      const response = await getOrdersInRangeApi(dateFrom, dateTo, token);
      setLoading(false);
      setOrders(response);
    } catch (error) {
      setLoading(false);
      setError(error);
    }
  };

  return {
    loading,
    error,
    orders,
    getOrdersByTable,
    checkDeliveredOrder,
    addOrderToTable,
    addPaymentToOrder,
    closeOrder,
    deleteOrder,
    getOrdersByPayment,
    getOrdersInRange,
    getOpenOrders,
    markOrderReady,
    getCancellations,
    getCourtesies,
  };
}
